/**
 * SimpleAccordion Component which inherits from BaseAccordion
 * The main differences between all inherited accordions is applied styling and the header rendering.
 * This is the most standard design used throughout the entire app
 */

import React, { ReactNode } from 'react'
import BaseAccordion from './BaseAccordion'
import CustomIcons from './CustomIcons'
import {
  AccordionContainer,
  PanelContainer,
  HeaderText,
  IconContainer,
  PanelHeader,
} from './styles/SimpleAccordion.styles'

interface SimpleAccordionProps {
  children: ReactNode
  id: string
  defaultActiveKey?: string | string[] | null
  contextIdGenerated?: (id: string) => void
}

interface SimplePanelProps {
  header?: string | ReactNode
  children: ReactNode
  color?: string
  eventKey?: string
  noCollapse?: boolean
}

const SimpleAccordion: React.FC<SimpleAccordionProps> = ({
  children,
  id,
  ...props
}) => {
  return (
    <AccordionContainer>
      <BaseAccordion id={id} {...props}>
        {children}
      </BaseAccordion>
    </AccordionContainer>
  )
}

/**
 * Panel subcomponent for SimpleAccordion
 * Renders a header with optional text and chevron indicator
 * @param {string} color - Optional background color for the panel
 */
const Panel: React.FC<SimplePanelProps> = ({
  header,
  children,
  color,
  ...props
}) => {
  return (
    <PanelContainer color={color}>
      <BaseAccordion.Panel
        {...props}
        renderHeader={({ isActive, onToggle, noCollapse }) => (
          <PanelHeader>
            {header && <HeaderText>{header}</HeaderText>}
            {!noCollapse && (
              <IconContainer onClick={onToggle}>
                {isActive ? (
                  <CustomIcons.ChevronUpIcon size="xs" />
                ) : (
                  <CustomIcons.ChevronDownIcon size="xs" />
                )}
              </IconContainer>
            )}
          </PanelHeader>
        )}
      >
        {header && <div style={{ height: '2rem' }}></div>}
        {children}
      </BaseAccordion.Panel>
    </PanelContainer>
  )
}

// Create the compound component
const SimpleAccordionCompound = SimpleAccordion as typeof SimpleAccordion & {
  Panel: typeof Panel
}

SimpleAccordionCompound.Panel = Panel

export default SimpleAccordionCompound
